import { inject, Injectable, NgZone } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Storage } from '@ionic/storage';
import { v4 as uuidv4 } from 'uuid';

interface CordovaDiagnosticPlugin {
  isRemoteNotificationsEnabled(
    successCallback: (enabled: boolean) => void,
    errorCallback: (error: unknown) => void
  ): void;
  requestRemoteNotificationsAuthorization(
    successCallback: (status: string) => void,
    errorCallback: (error: unknown) => void,
    types: string[],
    omitRegistration: boolean
  ): void;
  requestRuntimePermission(
    successCallback: (status: string) => void,
    errorCallback: (error: unknown) => void,
    permission: string
  ): void;
  remoteNotificationType: { ALERT: string; BADGE: string; SOUND: string };
  permissionStatus: { GRANTED: string };
  permission: { POST_NOTIFICATIONS: string };
}

interface CordovaWithDiagnostic {
  plugins?: {
    diagnostic?: CordovaDiagnosticPlugin;
  };
}

interface NotificationOpenedEvent {
  type: 'notificationOpened';
  timeStamp: number;
  values: {
    alert?: string;
    title?: string;
    url?: string;
    type: 'cloudPage' | 'openDirect' | 'other';
    [key: string]: unknown;
  };
}

interface UrlActionEvent {
  type: 'urlAction';
  url: string;
}

declare var MCCordovaPlugin: {
  setContactKey(contactId: string, success: () => void, error: (err: unknown) => void): void;
  enablePush(success: () => void, error: (err: unknown) => void): void;
  disablePush(success: () => void, error: (err: unknown) => void): void;
  isPushEnabled(
    success: (enabled: boolean | number | { enabled: boolean }) => void,
    error: (err: unknown) => void
  ): void;
  getAttributes(success: (attrs: Record<string, string>) => void, error: (err: unknown) => void): void;
  clearAttribute(key: string, success: () => void, error: (err?: unknown) => void): void;
  getTags(success: (tags: string[]) => void, error: (err: unknown) => void): void;
  removeTag(tag: string, success: () => void, error: (err?: unknown) => void): void;
  setAttribute(key: string, value: string, success: () => void, error: (err: unknown) => void): void;
  getDeviceId(success: (id: string) => void, error: (err: unknown) => void): void;
  getSystemToken(success: (token: string) => void, error: (err: unknown) => void): void;
  getContactKey(success: (key: string) => void, error: (err: unknown) => void): void;
  setOnNotificationOpenedListener(listener: (event: NotificationOpenedEvent) => void): void;
  setOnUrlActionListener(listener: (event: UrlActionEvent) => void): void;
};

const ANONYMOUS_ID_STORAGE_KEY = 'mc_anonymous_id';
const LAST_USER_ID_STORAGE_KEY = 'mc_last_user_id';

/**
 * Converts a Cordova callback-style call into a Promise.
 * Usage: await cordovaExec<string>((ok, fail) => MCCordovaPlugin.getDeviceId(ok, fail));
 */
function cordovaExec<T = void>(
  pluginCall: (success: (result: T) => void, error: (err: unknown) => void) => void
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    pluginCall(
      (result) => resolve(result),
      (err) => reject(err)
    );
  });
}

@Injectable({
  providedIn: 'root',
})
export class MarketingCloudService {
  private platform = inject(Platform);
  private storage = inject(Storage);
  private ngZone = inject(NgZone);

  async initializeForUser(studentOrContactId: string) {
    if (!this.isMarketingCloudReady() || !studentOrContactId) {
      return;
    }

    await this.handleUserSwitch(studentOrContactId);
    await this.setMarketingCloudContactKey(studentOrContactId);
    await this.ensurePushEnabled();
  }

  private async handleUserSwitch(newUserId: string): Promise<void> {
    try {
      const lastUserId = await this.storage.get(LAST_USER_ID_STORAGE_KEY);

      if (lastUserId && lastUserId !== newUserId) {
        const newAnonymousId = uuidv4();
        await this.storage.set(ANONYMOUS_ID_STORAGE_KEY, newAnonymousId);
      }

      await this.storage.set(LAST_USER_ID_STORAGE_KEY, newUserId);
    } catch (error) {
      console.error('[MC] Error handling user switch:', error);
    }
  }

  private async setMarketingCloudContactKey(contactId: string): Promise<boolean> {
    if (!this.isMarketingCloudReady()) {
      return false;
    }

    try {
      console.log(`[MC] Setting contact key to ${contactId}`);
      await cordovaExec((ok, fail) => MCCordovaPlugin.setContactKey(contactId, ok, fail));
      return true;
    } catch (error) {
      console.error('[MC] Error setting contact key:', error);
      return false;
    }
  }

  private isMarketingCloudReady(): boolean {
    if (!this.platform.is('cordova')) {
      return false;
    }

    return typeof MCCordovaPlugin !== 'undefined' && (this.platform.is('ios') || this.platform.is('android'));
  }

  /**
   * Check if OS-level notification permission is granted.
   * This checks the actual device settings, not just the SDK state.
   */
  private getDiagnosticPlugin(): CordovaDiagnosticPlugin | null {
    const cordova = (window as unknown as { cordova?: CordovaWithDiagnostic }).cordova;
    return cordova?.plugins?.diagnostic ?? null;
  }

  private async checkNotificationPermission(): Promise<boolean> {
    if (!this.platform.is('cordova')) {
      return false;
    }

    const diagnostic = this.getDiagnosticPlugin();
    if (!diagnostic) {
      return false;
    }

    try {
      return await cordovaExec<boolean>((ok, fail) => diagnostic.isRemoteNotificationsEnabled(ok, fail));
    } catch (error) {
      console.error('[MC] Error checking notification permission:', error);
      return false;
    }
  }

  /**
   * Request OS-level notification permission from the user.
   * This shows the native permission dialog.
   *
   * IMPORTANT:
   * - iOS: Uses requestRemoteNotificationsAuthorization from diagnostic plugin
   * - Android 13+: Uses requestRuntimePermission for POST_NOTIFICATIONS
   * - Android <13: Notifications enabled by default, no permission needed
   */
  private async requestNotificationPermission(): Promise<boolean> {
    if (!this.platform.is('cordova')) {
      return false;
    }

    const diagnostic = this.getDiagnosticPlugin();
    if (!diagnostic) {
      return false;
    }

    try {
      let status: string;

      if (this.platform.is('ios')) {
        const types = [
          diagnostic.remoteNotificationType.ALERT,
          diagnostic.remoteNotificationType.BADGE,
          diagnostic.remoteNotificationType.SOUND,
        ];
        status = await cordovaExec<string>((ok, fail) =>
          diagnostic.requestRemoteNotificationsAuthorization(ok, fail, types, false)
        );
      } else if (this.platform.is('android')) {
        status = await cordovaExec<string>((ok, fail) =>
          diagnostic.requestRuntimePermission(ok, fail, diagnostic.permission.POST_NOTIFICATIONS)
        );
      } else {
        return false;
      }

      return status === diagnostic.permissionStatus.GRANTED;
    } catch (error) {
      console.error('[MC] Error requesting notification permission:', error);
      return false;
    }
  }

  async activateNotification(): Promise<boolean> {
    if (!this.isMarketingCloudReady()) {
      return false;
    }

    const hasPermission = await this.checkNotificationPermission();

    if (!hasPermission) {
      const granted = await this.requestNotificationPermission();
      if (!granted) {
        return false;
      }
    }

    try {
      console.log('[MC] Enabling push notifications');
      await cordovaExec((ok, fail) => MCCordovaPlugin.enablePush(ok, fail));
      return true;
    } catch (error) {
      console.error('[MC] Error enabling push:', error);
      return false;
    }
  }

  /**
   * Ensures push notifications are enabled if user has granted system permissions.
   * This checks the current state and enables push if needed.
   */
  private async ensurePushEnabled(): Promise<boolean> {
    if (!this.isMarketingCloudReady()) {
      return false;
    }

    const hasOSPermission = await this.checkNotificationPermission();
    if (!hasOSPermission) {
      return false;
    }

    const isCurrentlyEnabled = await this.isSubscribedToNotifications();
    if (isCurrentlyEnabled) {
      return true;
    }

    try {
      console.log('[MC] Enabling push notifications to sync with OS permission');
      await cordovaExec((ok, fail) => MCCordovaPlugin.enablePush(ok, fail));
      await this.iosEnsureApnsRegistered();
      return true;
    } catch (error) {
      console.error('[MC] Error enabling push:', error);
      return false;
    }
  }

  /**
   * iOS-only: ensure the APNs device token (the MC "System Token") has been
   * acquired and forwarded to the SFMC SDK. The SDK's auto-registration is
   * disabled in MCCordovaPlugin and the consent modal is the only path that
   * triggers `[UIApplication registerForRemoteNotifications]`, so users who
   * have iOS permission granted but never (or no longer) go through the modal
   * end up registered in MC with an empty System Token. Calling
   * `requestRemoteNotificationsAuthorization` with `omitRegistration: false`
   * when permission is already authorized re-runs the registration silently
   * (no UI prompt) — APNs returns the token, the AppDelegate forwards it via
   * `[mp setDeviceToken:]`, and MC populates the System Token field.
   */
  private async iosEnsureApnsRegistered(): Promise<void> {
    if (!this.platform.is('cordova') || !this.platform.is('ios')) {
      return;
    }

    if (typeof MCCordovaPlugin === 'undefined') {
      return;
    }

    const diagnostic = this.getDiagnosticPlugin();
    if (!diagnostic) {
      return;
    }

    try {
      const systemToken = await cordovaExec<string>((ok, fail) => MCCordovaPlugin.getSystemToken(ok, fail));
      if (systemToken) {
        return;
      }

      const hasOSPermission = await this.checkNotificationPermission();
      if (!hasOSPermission) {
        return;
      }

      console.log('[MC] System Token missing while OS permission granted — re-registering with APNs');
      const types = [
        diagnostic.remoteNotificationType.ALERT,
        diagnostic.remoteNotificationType.BADGE,
        diagnostic.remoteNotificationType.SOUND,
      ];
      await cordovaExec<string>((ok, fail) =>
        diagnostic.requestRemoteNotificationsAuthorization(ok, fail, types, false)
      );
    } catch (error) {
      console.error('[MC] Error ensuring APNs registration:', error);
    }
  }

  async isSubscribedToNotifications(): Promise<boolean> {
    if (!this.isMarketingCloudReady()) {
      return false;
    }

    try {
      const enabled = await cordovaExec<boolean | number | { enabled: boolean }>((ok, fail) =>
        MCCordovaPlugin.isPushEnabled(ok, fail)
      );
      // Convert to proper boolean: 1, true, or {enabled: true} => true, everything else => false
      return (
        enabled === 1 ||
        enabled === true ||
        (typeof enabled === 'object' && enabled !== null && enabled.enabled === true)
      );
    } catch (error) {
      console.error('[MC] Error checking push status:', error);
      return false;
    }
  }

  async handleUserLogout() {
    if (!this.isMarketingCloudReady()) {
      return;
    }

    try {
      await this.clearAllAttributes();
      await this.clearAllTags();

      const anonymousId = await this.getOrCreateAnonymousId();
      console.log(`[MC] Setting contact key to anonymous ID ${anonymousId} on logout`);
      await cordovaExec((ok, fail) => MCCordovaPlugin.setContactKey(anonymousId, ok, fail));
    } catch (error) {
      console.error('[MC] Error during logout:', error);
    }
  }

  public async setAttribute(key: string, value: string): Promise<boolean> {
    if (!this.isMarketingCloudReady()) {
      return false;
    }

    try {
      await cordovaExec((ok, fail) => MCCordovaPlugin.setAttribute(key, value, ok, fail));
      return true;
    } catch (error) {
      console.error(`[MC] Error setting attribute ${key}:`, error);
      return false;
    }
  }

  private async clearAllAttributes(): Promise<void> {
    try {
      const attributes = await cordovaExec<Record<string, string>>((ok, fail) =>
        MCCordovaPlugin.getAttributes(ok, fail)
      );
      await Promise.all(
        Object.keys(attributes).map((key) =>
          cordovaExec((ok, fail) => MCCordovaPlugin.clearAttribute(key, ok, fail)).catch(() => {})
        )
      );
    } catch {
      // Silently ignore — best-effort cleanup
    }
  }

  private async clearAllTags(): Promise<void> {
    try {
      const tags = await cordovaExec<string[]>((ok, fail) => MCCordovaPlugin.getTags(ok, fail));
      await Promise.all(
        tags.map((tag) => cordovaExec((ok, fail) => MCCordovaPlugin.removeTag(tag, ok, fail)).catch(() => {}))
      );
    } catch {
      // Silently ignore — best-effort cleanup
    }
  }

  public async getMarketingCloudAttributes(): Promise<Record<string, string>> {
    if (!this.isMarketingCloudReady()) {
      throw new Error('MarketingCloud not ready');
    }

    try {
      return await cordovaExec<Record<string, string>>((ok, fail) => MCCordovaPlugin.getAttributes(ok, fail));
    } catch (error) {
      console.error('[MC] Error getting attributes:', error);
      throw error;
    }
  }

  /**
   * Sync SDK push state with OS permission state.
   * This handles cases where the app was killed while user changed permissions in settings.
   */
  async syncPushStateWithOSPermission(): Promise<void> {
    if (!this.isMarketingCloudReady()) {
      return;
    }

    const osPermission = await this.checkNotificationPermission();
    const sdkPushEnabled = await this.isSubscribedToNotifications();

    try {
      if (osPermission && !sdkPushEnabled) {
        console.log('[MC] Enabling push notifications to sync with OS permission');
        await cordovaExec((ok, fail) => MCCordovaPlugin.enablePush(ok, fail));
      } else if (!osPermission && sdkPushEnabled) {
        console.log('[MC] Disabling push notifications to sync with OS permission');
        await cordovaExec((ok, fail) => MCCordovaPlugin.disablePush(ok, fail));
      }

      if (osPermission) {
        await this.iosEnsureApnsRegistered();
      }
    } catch (error) {
      console.error('[MC] Error syncing push state:', error);
    }
  }

  /**
   * Handle app resume: set contact key if available, then sync push state with OS permission.
   * Called from app.component.ts on platform.resume.
   */
  async handleAppResume(personContactId: string | null): Promise<void> {
    if (!this.isMarketingCloudReady()) {
      return;
    }

    if (personContactId) {
      await this.setMarketingCloudContactKey(personContactId);
    }

    await this.syncPushStateWithOSPermission();
  }

  /**
   * Get comprehensive diagnostics about the device's Marketing Cloud state.
   * Useful for debugging push notification issues.
   */
  public async getDeviceDiagnostics(): Promise<Record<string, unknown>> {
    if (!this.isMarketingCloudReady()) {
      return { error: 'SDK not ready' };
    }

    const safeExec = async <T>(fn: () => Promise<T>): Promise<{ value?: T; error?: unknown }> => {
      try {
        return { value: await fn() };
      } catch (error) {
        return { error };
      }
    };

    const [osPermission, deviceId, systemToken, contactKey, pushEnabled, attributes, tags, storageValues] =
      await Promise.all([
        this.platform.is('android') || this.platform.is('ios') ? this.checkNotificationPermission() : undefined,
        safeExec(() => cordovaExec<string>((ok, fail) => MCCordovaPlugin.getDeviceId(ok, fail))),
        safeExec(() => cordovaExec<string>((ok, fail) => MCCordovaPlugin.getSystemToken(ok, fail))),
        safeExec(() => cordovaExec<string>((ok, fail) => MCCordovaPlugin.getContactKey(ok, fail))),
        this.isSubscribedToNotifications(),
        safeExec(() => cordovaExec<Record<string, string>>((ok, fail) => MCCordovaPlugin.getAttributes(ok, fail))),
        safeExec(() => cordovaExec<string[]>((ok, fail) => MCCordovaPlugin.getTags(ok, fail))),
        Promise.all([this.storage.get(LAST_USER_ID_STORAGE_KEY), this.storage.get(ANONYMOUS_ID_STORAGE_KEY)]),
      ]);

    return {
      timestamp: new Date().toISOString(),
      platform: this.platform.platforms(),
      osNotificationPermission: osPermission,
      deviceId: deviceId.value,
      deviceIdError: deviceId.error,
      systemToken: systemToken.value,
      hasSystemToken: !!systemToken.value,
      systemTokenError: systemToken.error,
      contactKey: contactKey.value,
      contactKeyError: contactKey.error,
      pushEnabled,
      attributes: attributes.value,
      attributesError: attributes.error,
      tags: tags.value,
      tagsError: tags.error,
      storage: { lastUserId: storageValues[0], anonymousId: storageValues[1] },
    };
  }

  /**
   * Register listeners for notification open and URL action events.
   * Must be called after platform.ready() to ensure Cordova plugins are available.
   * The MC plugin caches events if listeners aren't registered yet, so no event is lost.
   *
   * @param onNotificationUrl Called with the parsed route or raw URL when a notification is opened.
   *   - route: internal app route (e.g. "dashboard") or null for external URLs
   *   - url: the original raw URL from the notification
   */
  registerNotificationListeners(onNotificationUrl: (route: string | null, url: string) => void): void {
    if (!this.isMarketingCloudReady()) {
      return;
    }

    MCCordovaPlugin.setOnNotificationOpenedListener((event) => {
      console.log('[MC] Notification opened:', event.values?.type);
      const url = event.values?.url;
      if (url) {
        this.ngZone.run(() => onNotificationUrl(this.extractAppRoute(url), url));
      }
    });

    MCCordovaPlugin.setOnUrlActionListener((event) => {
      console.log('[MC] URL action received:', event.url);
      if (event.url) {
        this.ngZone.run(() => onNotificationUrl(this.extractAppRoute(event.url), event.url));
      }
    });
  }

  /**
   * Extract an internal app route from a URL.
   * Supports:
   * - https://app.envoituresimone.com/dashboard → "dashboard"
   * - evs://code → "code"
   * Returns null for external URLs (e.g. https://www.envoituresimone.com/..., https://aide.envoituresimone.com/...).
   */
  private extractAppRoute(url: string): string | null {
    // Only match app.envoituresimone.com (www is treated as external), with optional /ul/ prefix
    const appMatch = url.match(/app\.envoituresimone\.com(?:\/ul)?\/(.+)/);
    if (appMatch) {
      return appMatch[1].split('?')[0];
    }

    // Custom scheme: evs://route
    const evsMatch = url.match(/^evs:\/\/(.+)/);
    if (evsMatch) {
      return evsMatch[1].split('?')[0];
    }

    return null;
  }

  private async getOrCreateAnonymousId(): Promise<string> {
    try {
      let anonymousId = await this.storage.get(ANONYMOUS_ID_STORAGE_KEY);

      if (!anonymousId) {
        anonymousId = uuidv4();
        await this.storage.set(ANONYMOUS_ID_STORAGE_KEY, anonymousId);
      }

      return anonymousId;
    } catch (error) {
      console.error('[MC] Error getting/creating anonymous ID:', error);
      return uuidv4();
    }
  }
}
